import { useEffect, useCallback, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import PrivateOutlet from "./layout/PrivateOutlet";
import StandaloneOutlet from "./layout/StandaloneOutlet";
import { StandaloneProvider } from "./context/StandaloneContext";
import useStandaloneAuth from "./hooks/useStandaloneAuth";

import store, { saveState } from "./redux/store";
import { connectOdds, disconnectOdds } from "./utils/constants";
import { preloadDiamondStreamUrls, loadGameCatalog, getGameKeyByLink } from "./utils/gameCatalog";
import { setAuthDetails, setUserData, setAllowedGames } from "./redux/features/accountSlice";
import { setGameKey } from "./redux/features/gameSlice";

import ErrorBoundary from "./components/ErrorBoundary";
import UnAuthorized from "./components/UnAuthorized";
import PremiumGameLoader, {
  getGameNameFromPath,
} from "./components/PremiumGameLoader";
import Launch from "./pages/launch";
import { userAuth } from "./service/AuthService";
// getService is still used by casino bet APIs (service/casino/index.js)
// but no longer needed for WebSocket connections
import { IS_MOCK, MOCK_USER_DATA } from "./utils/mockData";
import {
  initBridge,
  destroyBridge,
  emitCasinoReady,
  emitGameOpened,
  emitGameClosed,
  onParentMessage,
  offParentMessage,
} from "./utils/postMessageBridge";

// Lazy-loaded game components
const CasinoList = lazy(() => import("./pages/casino/CasinoList"));
const Cards32A = lazy(() => import("./pages/casino/games/32Cards-A"));
const Cards32B = lazy(() => import("./pages/casino/games/32Cards-B"));
const JudgementCards3 = lazy(
  () => import("./pages/casino/games/3CardsJudgement"),
);
const AmarAkbarAnthony = lazy(
  () => import("./pages/casino/games/AmabAkbarAnthony"),
);
const AndarBahar = lazy(() => import("./pages/casino/games/AndarBahar"));
const AndarBahar2 = lazy(() => import("./pages/casino/games/AndarBahar-2"));
const BollywoodCasino = lazy(
  () => import("./pages/casino/games/BollywoodCasino"),
);
const CasinoMeter = lazy(() => import("./pages/casino/games/CasinoMeter"));
const CasinoQueen = lazy(() => import("./pages/casino/games/CasinoQueen"));
const DragonTiger1Day = lazy(
  () => import("./pages/casino/games/DragonTiger1Day"),
);
const DragonTiger202 = lazy(
  () => import("./pages/casino/games/DragonTiger202"),
);
const DragonTiger201 = lazy(
  () => import("./pages/casino/games/DragonTiger201"),
);
const InstantWorli = lazy(() => import("./pages/casino/games/InstantWorli"));
const Lucky7A = lazy(() => import("./pages/casino/games/Lucky7A"));
const Lucky7B = lazy(() => import("./pages/casino/games/Lucky7B"));
const Poker20 = lazy(() => import("./pages/casino/games/Poker20-20"));
const Race2020 = lazy(() => import("./pages/casino/games/Race2020"));
const TeenPatti = lazy(() => import("./pages/casino/games/TeenPatti"));
const TeenPatti1Day = lazy(() => import("./pages/casino/games/Teenpatti1Day"));
const CasinoResult = lazy(() => import("./pages/casinoResults/CasinoResults"));
const DragonTigerLion = lazy(
  () => import("./pages/casino/games/DragonTigerLion"),
);
const TeenPattiTest = lazy(() => import("./pages/casino/games/TeenPattiTest"));
const TeenPattiOpen = lazy(() => import("./pages/casino/games/TeenPattiOpen"));
const CasinoWar = lazy(() => import("./pages/casino/games/CasinoWar"));
const Cricket2020 = lazy(() => import("./pages/casino/games/Cricket20-20"));
const Poker1Day = lazy(() => import("./pages/casino/games/Poker1Day"));
const Pokerr6Players = lazy(() => import("./pages/casino/games/Poker6Players"));
const Baccart = lazy(() => import("./pages/casino/games/Baccart"));
const Baccart2 = lazy(() => import("./pages/casino/games/Baccarat2"));
const Worli = lazy(() => import("./pages/casino/games/WorliMatka"));
const FiveCricket = lazy(() => import("./pages/casino/games/Five-5Cricket"));
const SuperOver = lazy(() => import("./pages/casino/games/SuperOver"));

// Casino Lobby
const CasinoLobby = lazy(() => import("./pages/lobby/CasinoLobby"));

// In-House Games
const InHouseLobby = lazy(() => import("./pages/inhouse/InHouseLobby"));
const DiceGame = lazy(() => import("./pages/inhouse/games/Dice"));
const ColorPredictionGame = lazy(() => import("./pages/inhouse/games/ColorPrediction"));
const FortuneWheelGame = lazy(() => import("./pages/inhouse/games/FortuneWheel"));
// Crash route redirects to Aviator (same backend engine)
const CrashRedirect = lazy(() => import("./pages/inhouse/games/Aviator"));
const RouletteGame = lazy(() => import("./pages/inhouse/games/Roulette"));
const AndarBaharGame = lazy(() => import("./pages/inhouse/games/AndarBahar"));
const CasinoDiceGame = lazy(() => import("./pages/inhouse/games/CasinoDice"));
const KenoGame = lazy(() => import("./pages/inhouse/games/Keno"));
const AviatorGame = lazy(() => import("./pages/inhouse/games/Aviator"));
const FourInARowGame = lazy(() => import("./pages/inhouse/games/FourInARow"));
const MinesGame = lazy(() => import("./pages/inhouse/games/Mines"));
const PlinkoGame = lazy(() => import("./pages/inhouse/games/Plinko"));

function LoadingFallback() {
  const location = useLocation();
  const isInHouse = location.pathname.startsWith("/inhouse-games");

  return (
    <PremiumGameLoader
      gameName={isInHouse ? getGameNameFromPath(location.pathname) : "Casino"}
    />
  );
}

function GameRoutes() {
  return (
    <Routes>
      <Route exact path="/" element={<Launch />} />
      <Route exact path="/un-auth" element={<UnAuthorized />} />
      <Route path="/live-casino" element={<CasinoList />} />
      <Route path="/live-casino/teenpatti/20-20" element={<TeenPatti />} />
      <Route path="/live-casino/teenpatti/1-day" element={<TeenPatti1Day />} />
      <Route path="/live-casino/teenpatti/test" element={<TeenPattiTest />} />
      <Route path="/live-casino/teenpatti/open" element={<TeenPattiOpen />} />
      <Route
        path="/live-casino/dragon-tiger/20-20"
        element={<DragonTiger201 />}
      />
      <Route
        path="/live-casino/dragon-tiger/20-20/2"
        element={<DragonTiger202 />}
      />
      <Route path="/live-casino/card-32/a" element={<Cards32A />} />
      <Route path="/live-casino/card-32/b" element={<Cards32B />} />
      <Route path="/live-casino/race/20-20" element={<Race2020 />} />
      <Route path="/live-casino/cn/queen" element={<CasinoQueen />} />
      <Route
        path="/live-casino/dragon-tiger/1-day"
        element={<DragonTiger1Day />}
      />
      <Route path="/live-casino/lucky7/a" element={<Lucky7A />} />
      <Route path="/live-casino/lucky7/b" element={<Lucky7B />} />
      <Route path="/live-casino/andar-bahar" element={<AndarBahar />} />
      <Route path="/live-casino/andar-bahar/2" element={<AndarBahar2 />} />
      <Route
        path="/live-casino/3cardsjudgement"
        element={<JudgementCards3 />}
      />
      <Route
        path="/live-casino/cn/bollywood-table"
        element={<BollywoodCasino />}
      />
      <Route
        path="/live-casino/andar-bahar/aaa"
        element={<AmarAkbarAnthony />}
      />
      <Route path="/live-casino/poker/20-20" element={<Poker20 />} />
      <Route path="/live-casino/worli/2" element={<InstantWorli />} />
      <Route path="/live-casino/cn/meter" element={<CasinoMeter />} />
      <Route
        path="/live-casino/dragon-tiger/dtl"
        element={<DragonTigerLion />}
      />
      <Route path="/live-casino/cn/war" element={<CasinoWar />} />
      <Route path="/live-casino/cricket/five" element={<FiveCricket />} />
      <Route path="/live-casino/cricket/20-20" element={<Cricket2020 />} />
      <Route path="/live-casino/poker" element={<Poker1Day />} />
      <Route
        path="/live-casino/poker/poker-6-player"
        element={<Pokerr6Players />}
      />
      <Route path="/live-casino/baccarat" element={<Baccart />} />
      <Route path="/live-casino/baccarat/2" element={<Baccart2 />} />
      <Route path="/live-casino/worli" element={<Worli />} />
      <Route path="/live-casino/cricket/super-over" element={<SuperOver />} />
      <Route path="/report/casino-result" element={<CasinoResult />} />

      {/* Casino Lobby */}
      <Route path="/lobby" element={<CasinoLobby />} />

      {/* In-House Games */}
      <Route path="/inhouse-games" element={<InHouseLobby />} />
      <Route path="/inhouse-games/dice" element={<DiceGame />} />
      <Route path="/inhouse-games/color-prediction" element={<ColorPredictionGame />} />
      <Route path="/inhouse-games/fortune-wheel" element={<FortuneWheelGame />} />
      <Route path="/inhouse-games/crash" element={<CrashRedirect />} />
      <Route path="/inhouse-games/roulette" element={<RouletteGame />} />
      <Route path="/inhouse-games/andar-bahar" element={<AndarBaharGame />} />
      <Route path="/inhouse-games/casino-dice" element={<CasinoDiceGame />} />
      <Route path="/inhouse-games/keno" element={<KenoGame />} />
      <Route path="/inhouse-games/aviator" element={<AviatorGame />} />
      <Route path="/inhouse-games/4-in-a-row" element={<FourInARowGame />} />
      <Route path="/inhouse-games/mines" element={<MinesGame />} />
      <Route path="/inhouse-games/plinko" element={<PlinkoGame />} />
    </Routes>
  );
}

function AppInner() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { gameKey } = useSelector((state) => state.gameData);
  const location = useLocation();
  const navigate = useNavigate();
  const { isStandalone } = useStandaloneAuth();

  // Initialize postMessage bridge
  useEffect(() => {
    initBridge();

    const handleForceLogout = () => {
      localStorage.clear();
      dispatch({ type: "accountData/logOut", payload: {} });
      dispatch({ type: "gameData/logOut", payload: {} });
      navigate("/un-auth");
    };

    onParentMessage("FORCE_LOGOUT", handleForceLogout);

    return () => {
      offParentMessage("FORCE_LOGOUT", handleForceLogout);
      destroyBridge();
    };
  }, [dispatch, navigate]);

  // Preload dynamic game codes and Diamond Casino stream URLs on startup
  useEffect(() => {
    loadGameCatalog();
    preloadDiamondStreamUrls();
  }, []);

  // Auto-detect gameKey from route and dispatch to Redux
  useEffect(() => {
    const detectedKey = getGameKeyByLink(location.pathname);
    if (detectedKey) {
      dispatch(setGameKey(detectedKey));
    }
  }, [dispatch, location.pathname]);

  // Diamond Casino STOMP WebSocket for live odds
  useEffect(() => {
    if (gameKey) {
      connectOdds(dispatch, gameKey);
    }

    return () => {
      disconnectOdds();
    };
  }, [dispatch, gameKey]);

  // Emit game opened/closed events based on route
  useEffect(() => {
    const isLiveCasinoGame =
      location.pathname.startsWith("/live-casino/") &&
      location.pathname !== "/live-casino";
    const isInHouseGame =
      location.pathname.startsWith("/inhouse-games/") &&
      location.pathname !== "/inhouse-games";

    if (isLiveCasinoGame && gameKey) {
      emitGameOpened(gameKey, location.pathname);
    } else if (isInHouseGame) {
      const gameName = location.pathname.split("/inhouse-games/")[1];
      emitGameOpened(gameName, location.pathname);
    } else if (
      location.pathname === "/live-casino" ||
      location.pathname === "/inhouse-games"
    ) {
      emitGameClosed();
    }
  }, [location.pathname, gameKey]);

  useEffect(() => {
    const authDetails = JSON.parse(localStorage.getItem("authDetails"));
    if (authDetails) {
      dispatch(setAuthDetails(authDetails));
      dispatch(setUserData(authDetails));
    }

    const storedAllowedGames = JSON.parse(localStorage.getItem("allowedGames"));
    if (storedAllowedGames) {
      dispatch(setAllowedGames(storedAllowedGames));
    }
  }, [dispatch]);

  const fetchData = useCallback(
    async (token, operatorId, homeUrl) => {
      const res = await userAuth(operatorId);

      if (res.status === 200) {
        const authDetails = { ...res.data.data, homeUrl, token };
        localStorage.setItem("authDetails", JSON.stringify(authDetails));
        dispatch(setAuthDetails({ ...res.data.data, homeUrl, token }));
        dispatch(setUserData({ ...res.data.data, homeUrl, token }));
        localStorage.setItem("authenticated", "yes");
        if (res.data.data?.allowedGames) {
          dispatch(setAllowedGames(res.data.data.allowedGames));
          localStorage.setItem(
            "allowedGames",
            JSON.stringify(res.data.data.allowedGames),
          );
        }
        emitCasinoReady(res.data.data);
        navigate(`/live-casino?operatorId=${operatorId}`);
      } else {
        navigate("/un-auth");
      }
    },
    [dispatch, navigate],
  );

  // Lobby auth flow — skip if standalone mode handles it
  useEffect(() => {
    if (isStandalone) return;

    if (location.pathname.includes("auth")) {
      const token = searchParams.get("token");
      const operatorId = searchParams.get("operatorId");
      const homeUrl = searchParams.get("homeUrl");

      if (token) {
        setTimeout(() => {
          fetchData(token, operatorId, homeUrl);
        }, 500);

        localStorage.setItem("token", token);
      }
    }
  }, [fetchData, location.pathname, searchParams, isStandalone]);

  useEffect(() => {
    const _authDetails = JSON.parse(localStorage.getItem("authDetails"));
    const operatorId =
      searchParams.get("operatorId") || _authDetails?.operatorId;

    if (operatorId)
      localStorage.setItem("operatorId", JSON.stringify(operatorId));
  }, [searchParams]);

  useEffect(() => {
    store.subscribe(() => {
      saveState(store.getState());
    });
  }, []);

  // Token check — skip redirect if standalone mode (hook handles auth)
  // Also skip for inhouse game routes so they can load without the casino backend
  useEffect(() => {
    if (isStandalone) return;

    const isInHouseRoute = location.pathname.startsWith("/inhouse-games");
    const isLobbyRoute = location.pathname === "/lobby";
    if (isInHouseRoute || isLobbyRoute) return;

    if (IS_MOCK) {
      localStorage.setItem("token", MOCK_USER_DATA.token);
      localStorage.setItem("authenticated", "yes");
      localStorage.setItem(
        "operatorId",
        JSON.stringify(MOCK_USER_DATA.operatorId),
      );
      localStorage.setItem("authDetails", JSON.stringify(MOCK_USER_DATA));
      dispatch(setAuthDetails(MOCK_USER_DATA));
      dispatch(setUserData(MOCK_USER_DATA));
      emitCasinoReady(MOCK_USER_DATA);
      return;
    }
    const token = localStorage.getItem("token");
    const urlToken = searchParams.get("token");
    if (!token && !urlToken) navigate("/un-auth");
  }, [navigate, dispatch, isStandalone, searchParams, location.pathname]);

  const Outlet = isStandalone ? StandaloneOutlet : PrivateOutlet;

  return (
    <ErrorBoundary>
      <Outlet>
        <Suspense fallback={<LoadingFallback />}>
          <GameRoutes />
        </Suspense>
      </Outlet>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <StandaloneProvider>
      <AppInner />
    </StandaloneProvider>
  );
}
